import { doc, getDoc } from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import {
  AlertCircle,
  CircleUser,
  Building2,
  Globe,
  Images,
  Instagram,
  LayoutGrid,
  Loader2,
  LucideIcon,
  MessageSquareText,
  Palette,
  Pencil,
  Plus,
  X,
  Youtube,
} from "lucide-react";
import { FaLinkedin, FaTiktok, FaXTwitter } from "react-icons/fa6";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { useAuth } from "../contexts/AuthContext";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import {
  brandColorListToSettings,
  getBrandColorList,
  mergeBrandColorLists,
} from "../lib/brandColors";
import {
  commitBrandKitAssets,
  deleteDraftBrandKitAssets,
  extractBrandKitFromUrl,
  refreshBrandKitColorSystem,
  uploadBrandKitAsset,
} from "../lib/brandKitUtils";
import {
  getBrandKitAssetUploadErrorMessage,
  getBrandKitSaveErrorCopy,
} from "../lib/brandKitSaveErrors";
import { cn } from "../lib/utils";
import {
  BrandAsset,
  BrandKit,
  BrandKitColorSettings,
  BrandKitColorSystem,
  BrandKitFontSettings,
  BrandVoiceTone,
  PrimaryBrandElement,
} from "../models/BrandKit";
import { db, getSignedUrl, storage } from "../services/Firebase";
import { getAvailableFontsForUser } from "../utils/textOverlayUtils";
import { BrandColorSystemCard } from "./BrandColorSystemCard";
import { BrandPaletteEditor } from "./BrandPaletteEditor";
import { TemplateThumb } from "./TemplateThumb";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"; // Assuming shadcn/ui Select
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast"; // Import useToast
import { logoSurfaceClass, useLogoTone } from "../lib/logoTone";

type EditableBrandKit = Omit<
  BrandKit,
  "id" | "userId" | "createdAt" | "updatedAt"
>;

interface BrandKitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    finalKitData: Partial<EditableBrandKit>,
    files: {
      visualAsset?: File;
      cover?: File;
      bRoll?: File[];
    }
  ) => Promise<void>;
  initialData?: BrandKit | null; // For editing existing kit
}

const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024; // 50MB per video
const MAX_BROLL_VIDEOS = 50;
const MAX_BRAND_ASSETS = 30;
const MAX_ASSET_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const ALLOWED_ASSET_TYPES = ["image/png", "image/jpeg", "image/webp"];
const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo", // .avi
];

const NO_FONT_SELECTED_VALUE = "__NO_FONT_SELECTED__"; // Value for 'No Font' option

export const defaultFontSettings: BrandKitFontSettings = {
  primaryFont: null,
  secondaryFont: null,
  bodyFont: null,
};

export const defaultColorSettings: BrandKitColorSettings = {
  primaryColor: null,
  secondaryColor: null,
  tertiaryColor: null,
  additionalColors: [],
};

type EditorSection = "identity" | "business" | "style" | "voice" | "templates" | "assets";

const EDITOR_SECTIONS: {
  id: EditorSection;
  label: string;
  icon: LucideIcon;
}[] = [
  { id: "identity", label: "Identity", icon: CircleUser },
  { id: "business", label: "Business", icon: Building2 },
  { id: "style", label: "Colors & Type", icon: Palette },
  { id: "voice", label: "Voice", icon: MessageSquareText },
  { id: "templates", label: "Templates", icon: LayoutGrid },
  { id: "assets", label: "Assets", icon: Images },
];

const createDefaultBrandKit = (): EditableBrandKit => ({
  name: "",
  logoPath: undefined,
  brandName: undefined,
  tagline: undefined,
  website: undefined,
  primarySocial: undefined,
  primaryBrandElement: "auto",
  fonts: defaultFontSettings,
  colors: defaultColorSettings,
  styleInstructions: undefined,
  brandVoiceTone: null,
  brandVoiceInstructions: undefined,
  allowedTemplateIds: undefined,
  bRollVideos: [],
});

// Template type for the template picker
interface TemplateInfo {
  id: string;
  name: string;
  supportedCategory: string;
  example?: { videoPath?: string; imagePaths?: string[] };
  firstSlideDimensions?: { width: number; height: number };
  coverTimestamp?: number;
}

const BRAND_VOICE_TONE_OPTIONS: { value: BrandVoiceTone; label: string }[] = [
  { value: null, label: "No preference" },
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
  { value: "friendly", label: "Friendly" },
  { value: "professional", label: "Professional" },
];

const PRIMARY_BRAND_ELEMENT_OPTIONS: {
  value: PrimaryBrandElement;
  label: string;
}[] = [
  {
    value: "auto",
    label: "Template default",
  },
  {
    value: "brandName",
    label: "Name",
  },
  {
    value: "website",
    label: "Website",
  },
  {
    value: "social",
    label: "Social",
  },
];

const MAX_INSTRUCTIONS_LENGTH = 1000;

type OverlayState = "input" | "loading" | "error";

const IMPORT_STEPS = [
  "Scanning website...",
  "Extracting colors & fonts...",
  "Analyzing brand identity...",
  "Downloading logo...",
  "Finding brand images...",
  "Almost done...",
];


interface BRollVideoPreview {
  file: File;
  preview: string;
  id: string;
}

export const BrandKitModal: React.FC<BrandKitModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [activeSection, setActiveSection] = useState<EditorSection>("identity");
  const kitNameInputRef = useRef<HTMLInputElement>(null);
  // Local view of the derived pairings — refreshed in place by "Pair now"
  const [derivedColorSystem, setDerivedColorSystem] = useState<
    BrandKitColorSystem | null | undefined
  >(undefined);
  const [isPairing, setIsPairing] = useState(false);
  const [newAudience, setNewAudience] = useState("");
  const [kitData, setKitData] = useState<Partial<EditableBrandKit>>(
    createDefaultBrandKit()
  );
  const [visualAssetFile, setVisualAssetFile] = useState<File | undefined>(
    undefined
  );
  const [localVisualAssetPreview, setVisualAssetPreview] = useState<
    string | undefined
  >(undefined);
  const {
    url: savedLogoUrl,
    loading: logoLoading,
    error: logoPreviewError,
    retry: retryLogo,
  } = useFirebaseUrl(isOpen && !visualAssetFile ? kitData.logoPath : undefined);
  const visualAssetPreview = localVisualAssetPreview || savedLogoUrl;
  const visualAssetTone = useLogoTone(visualAssetPreview);
  const [coverBgFile, setCoverBgFile] = useState<File | undefined>(undefined);
  const [coverBgPreview, setCoverBgPreview] = useState<string | undefined>(
    undefined
  );
  const [bRollVideos, setBRollVideos] = useState<BRollVideoPreview[]>([]);
  const [bRollError, setBRollError] = useState<string | null>(null);
  const [existingBRollPreviews, setExistingBRollPreviews] = useState<string[]>(
    []
  );
  // Brand assets state
  const [brandAssets, setBrandAssets] = useState<BrandAsset[]>([]);
  const [brandAssetThumbnails, setBrandAssetThumbnails] = useState<Record<string, string>>({});
  const [brandAssetError, setBrandAssetError] = useState<string | null>(null);
  const [uploadingAssets, setUploadingAssets] = useState<Set<string>>(new Set());
  // Pending changes - only committed on save
  const [pendingUploads, setPendingUploads] = useState<BrandAsset[]>([]);
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const hasSavedRef = useRef(false);
  // URL import state
  const [importUrl, setImportUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<string | null>(null);
  // Overlay state for new brand kit creation
  const [showOverlay, setShowOverlay] = useState(true);
  const [overlayState, setOverlayState] = useState<OverlayState>("input");
  const [importError, setImportError] = useState<string | null>(null);
  const [isBlockedError, setIsBlockedError] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [visualAssetError, setVisualAssetError] = useState<string | null>(null);
  const [coverBgError, setCoverBgError] = useState<string | null>(null);
  const { toast } = useToast();
  const [view, setView] = useState<"main">("main");
  const { currentUser, activeWorkspaceId } = useAuth();
  const availableFonts = getAvailableFontsForUser(activeWorkspaceId || undefined);

  // Template picker state
  const [availableTemplates, setAvailableTemplates] = useState<TemplateInfo[]>(
    []
  );
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    new Set()
  );
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>("");
  const [templatePreviewIndex, setTemplatePreviewIndex] = useState<
    Record<string, number>
  >({});
  const [firebaseUrlCache, setFirebaseUrlCache] = useState<
    Record<string, string>
  >({});
  const [restrictTemplates, setRestrictTemplates] = useState(false);
  // Look mode (`looks: true` from /api/templates): one gallery of looks, no category tabs.
  const [looks, setLooks] = useState(false);

  // Image cropper state for visual asset
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const visualAssetInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchCoverBgPreview = async (path: string) => {
      try {
        const url = await getSignedUrl(path);
        setCoverBgPreview(url);
      } catch (error) {
        console.error("Failed to fetch signed URL for cover bg:", error);
        setCoverBgPreview(undefined);
        toast({
          variant: "destructive",
          title: "Cover Image Error",
          description: "Could not load existing cover image preview.",
        });
      }
    };

    const fetchBRollPreviews = async (paths: string[]) => {
      try {
        const urls = await Promise.all(
          paths.map(async (path) => {
            try {
              return await getSignedUrl(path);
            } catch (error) {
              console.error(
                "Failed to fetch signed URL for B-Roll video:",
                error
              );
              return null;
            }
          })
        );
        setExistingBRollPreviews(urls.filter(Boolean) as string[]);
      } catch (error) {
        console.error("Failed to fetch B-Roll previews:", error);
        setExistingBRollPreviews([]);
      }
    };

    const fetchBrandAssetThumbnails = async (assets: BrandAsset[]) => {
      const thumbnails: Record<string, string> = {};
      await Promise.all(
        assets.map(async (asset) => {
          try {
            const path = asset.thumbnailPath || asset.storagePath;
            const url = await getSignedUrl(path);
            thumbnails[asset.id] = url;
          } catch (error) {
            console.error(`Failed to fetch thumbnail for asset ${asset.id}:`, error);
          }
        })
      );
      setBrandAssetThumbnails(thumbnails);
    };

    if (isOpen) {
      hasSavedRef.current = false;
      setVisualAssetError(null);
      setBRollError(null);
      setCoverBgError(null);
      setBrandAssetError(null);
      setVisualAssetFile(undefined);
      setCoverBgFile(undefined);
      setBRollVideos([]);
      setUploadingAssets(new Set());
      setPendingUploads([]);
      setPendingDeletions(new Set());
      setImportUrl("");
      setIsImporting(false);
      setImportProgress(null);
      setImportError(null);
      setIsBlockedError(false);
      setImportStep(0);
      setActiveSection("identity");
      setNewAudience("");
      setDerivedColorSystem(initialData?.colorSystem);
      setIsPairing(false);

      if (initialData) {
        const { id, userId, createdAt, updatedAt, ...editableData } =
          initialData;
        setKitData(editableData);

        // Editing existing kit — no overlay
        if (editableData.name) {
          setShowOverlay(false);
        } else {
          setShowOverlay(true);
          setOverlayState("input");
        }

        setVisualAssetPreview(undefined);

        if (editableData.bRollVideos && editableData.bRollVideos.length > 0) {
          fetchBRollPreviews(editableData.bRollVideos);
        } else {
          setExistingBRollPreviews([]);
        }

        // Initialize brand assets (deduplicate by ID in case of prior double-writes)
        if (editableData.brandAssets && editableData.brandAssets.length > 0) {
          const seen = new Set<string>();
          const dedupedAssets = editableData.brandAssets.filter((a) => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          });
          setBrandAssets(dedupedAssets);
          fetchBrandAssetThumbnails(dedupedAssets);
        } else {
          setBrandAssets([]);
          setBrandAssetThumbnails({});
        }

        // Initialize template selection from saved data
        if (
          editableData.allowedTemplateIds &&
          editableData.allowedTemplateIds.length > 0
        ) {
          setSelectedTemplateIds(new Set(editableData.allowedTemplateIds));
          setRestrictTemplates(true);
        } else {
          setSelectedTemplateIds(new Set());
          setRestrictTemplates(false);
        }
      } else {
        setKitData(createDefaultBrandKit());
        setVisualAssetPreview(undefined);
        setExistingBRollPreviews([]);
        setBrandAssets([]);
        setBrandAssetThumbnails({});
        setSelectedTemplateIds(new Set());
        setRestrictTemplates(false);
        setShowOverlay(true);
        setOverlayState("input");
      }
      setView("main");
    }
  }, [initialData, isOpen, toast]);

  // Auto-refresh brand assets status when there are pending/processing items
  useEffect(() => {
    if (!isOpen || !initialData?.id) return;

    const hasProcessingAssets = brandAssets.some(
      (a) => a.processingStatus === "pending" || a.processingStatus === "processing"
    );

    if (!hasProcessingAssets) return;

    // Immediate check on open/mount to avoid showing stale "processing" state
    refreshBrandAssets();

    const intervalId = setInterval(() => {
      refreshBrandAssets();
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(intervalId);
  }, [isOpen, initialData?.id, brandAssets]); // Depend on brandAssets to stop polling when done

  // Load templates when modal opens
  useEffect(() => {
    const loadTemplates = async () => {
      if (!isOpen) return;

      try {
        setTemplatesLoading(true);
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/templates?type=carousel`
        );
        if (resp.ok) {
          const data = await resp.json();
          const templates = Array.isArray(data.templates) ? data.templates : [];
          setAvailableTemplates(templates);
          setLooks(data.looks === true);
          // Initialize active category tab (none in look mode: one gallery)
          const firstCat = templates[0]?.supportedCategory || "";
          setActiveCategoryTab(data.looks === true ? "" : firstCat);
        } else {
          setAvailableTemplates([]);
          setActiveCategoryTab("");
        }
      } catch (e) {
        console.error("Error loading templates:", e);
        setAvailableTemplates([]);
        setActiveCategoryTab("");
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
  }, [isOpen]);

  // Advance through import progress steps during loading, stop at last step
  useEffect(() => {
    if (overlayState !== "loading") return;
    const interval = setInterval(() => {
      setImportStep((prev) =>
        prev < IMPORT_STEPS.length - 1 ? prev + 1 : prev
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [overlayState]);

  // Preload Firebase URLs for template images
  useEffect(() => {
    const loadUrls = async () => {
      const urlsToLoad: string[] = [];

      for (const template of availableTemplates) {
        if (template.example?.videoPath) {
          urlsToLoad.push(template.example.videoPath);
        }
        if (template.example?.imagePaths) {
          urlsToLoad.push(...template.example.imagePaths);
        }
      }

      // Load all URLs that aren't already cached
      const promises = urlsToLoad
        .filter((path) => !firebaseUrlCache[path])
        .map(async (path) => {
          try {
            const storageRef = ref(storage, path);
            const url = await getSignedUrl(storageRef.fullPath);
            return { path, url };
          } catch (error) {
            console.error(`Failed to load URL for ${path}:`, error);
            return null;
          }
        });

      const results = await Promise.all(promises);
      const newCache = { ...firebaseUrlCache };
      for (const result of results) {
        if (result) {
          newCache[result.path] = result.url;
        }
      }
      setFirebaseUrlCache(newCache);
    };

    if (availableTemplates.length > 0) {
      loadUrls();
    }
  }, [availableTemplates, firebaseUrlCache]);

  // Look mode: saved picks that are not looks (pruned or never seeded) drop
  // out, so saving the kit removes them.
  useEffect(() => {
    if (!looks || availableTemplates.length === 0) return;
    const ids = new Set(availableTemplates.map((t) => t.id));
    const kept = Array.from(selectedTemplateIds).filter((id) => ids.has(id));
    if (kept.length === selectedTemplateIds.size) return;
    setSelectedTemplateIds(new Set(kept));
    if (kept.length === 0) setRestrictTemplates(false);
    setKitData((prev) => ({
      ...prev,
      allowedTemplateIds: kept.length > 0 ? kept : undefined,
    }));
  }, [looks, availableTemplates, selectedTemplateIds]);

  // Template picker helper functions
  const categoriesForType = useMemo(() => {
    return Array.from(
      new Set(availableTemplates.map((t) => t.supportedCategory))
    );
  }, [availableTemplates]);

  const selectedCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const id of selectedTemplateIds) {
      const t = availableTemplates.find((x) => x.id === id);
      if (!t) continue;
      counts[t.supportedCategory] = (counts[t.supportedCategory] || 0) + 1;
    }
    return counts;
  }, [selectedTemplateIds, availableTemplates]);

  const filteredTemplates = useMemo(() => {
    return availableTemplates.filter(
      (t) => !activeCategoryTab || t.supportedCategory === activeCategoryTab
    );
  }, [availableTemplates, activeCategoryTab]);

  const handleSelectAllInCategory = useCallback(
    (cat: string) => {
      const next = new Set(selectedTemplateIds);
      availableTemplates
        .filter((t) => !cat || t.supportedCategory === cat)
        .forEach((t) => next.add(t.id));
      setSelectedTemplateIds(next);
      setKitData((prev) => ({ ...prev, allowedTemplateIds: Array.from(next) }));
    },
    [selectedTemplateIds, availableTemplates]
  );

  const handleClearCategory = useCallback(
    (cat: string) => {
      const next = new Set(
        Array.from(selectedTemplateIds).filter((id) => {
          const t = availableTemplates.find((x) => x.id === id);
          return t && !!cat && t.supportedCategory !== cat;
        })
      );
      setSelectedTemplateIds(next);
      setKitData((prev) => ({
        ...prev,
        allowedTemplateIds: next.size > 0 ? Array.from(next) : undefined,
      }));
    },
    [selectedTemplateIds, availableTemplates]
  );

  const handleTemplateToggle = useCallback(
    (templateId: string) => {
      const next = new Set(selectedTemplateIds);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      setSelectedTemplateIds(next);
      // Only update allowedTemplateIds if restriction is enabled
      if (restrictTemplates) {
        setKitData((prev) => ({
          ...prev,
          allowedTemplateIds: next.size > 0 ? Array.from(next) : undefined,
        }));
      }
    },
    [selectedTemplateIds, restrictTemplates]
  );

  const handleRestrictTemplatesToggle = useCallback(
    (enabled: boolean) => {
      setRestrictTemplates(enabled);
      if (enabled) {
        // When enabling, pre-select all templates so users can deselect what they don't want
        const allIds = new Set(availableTemplates.map((t) => t.id));
        setSelectedTemplateIds(allIds);
        setKitData((prev) => ({
          ...prev,
          allowedTemplateIds: Array.from(allIds),
        }));
      } else {
        // When disabling, clear the restriction
        setKitData((prev) => ({
          ...prev,
          allowedTemplateIds: undefined,
        }));
        setSelectedTemplateIds(new Set());
      }
    },
    [availableTemplates]
  );

  const handleTemplatePreviewChange = useCallback(
    (templateId: string, newIndex: number) => {
      setTemplatePreviewIndex((prev) => ({
        ...prev,
        [templateId]: newIndex,
      }));
    },
    []
  );

  const handleTemplatePreviewPrev = useCallback(
    (e: React.MouseEvent, templateId: string, totalSlides: number) => {
      e.stopPropagation();
      e.preventDefault();
      const currentIndex = templatePreviewIndex[templateId] ?? 0;
      const newIndex = currentIndex === 0 ? totalSlides - 1 : currentIndex - 1;
      handleTemplatePreviewChange(templateId, newIndex);
    },
    [templatePreviewIndex, handleTemplatePreviewChange]
  );

  const handleTemplatePreviewNext = useCallback(
    (e: React.MouseEvent, templateId: string, totalSlides: number) => {
      e.stopPropagation();
      e.preventDefault();
      const currentIndex = templatePreviewIndex[templateId] ?? 0;
      const newIndex = currentIndex === totalSlides - 1 ? 0 : currentIndex + 1;
      handleTemplatePreviewChange(templateId, newIndex);
    },
    [templatePreviewIndex, handleTemplatePreviewChange]
  );

  // Called when crop is complete (user finished dragging/zooming)
  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Called when image loads - calculate zoom to fit entire image in crop area
  const onMediaLoaded = useCallback((mediaSize: { width: number; height: number }) => {
    // Calculate the zoom needed to fit the entire image inside the crop circle
    // The crop area is square (aspect=1), so we need to fit the larger dimension
    const { width, height } = mediaSize;
    const maxDimension = Math.max(width, height);
    const minDimension = Math.min(width, height);

    // Initial zoom: fit the entire image (larger dimension) inside the crop area
    // When zoom=1, the smaller dimension fills the crop area
    // To fit larger dimension, we need zoom = minDimension / maxDimension
    const fitZoom = minDimension / maxDimension;

    // Allow zooming out even more (to 50% of fit zoom) for flexibility
    const calculatedMinZoom = fitZoom * 0.5;

    setMinZoom(calculatedMinZoom);
    setZoom(fitZoom); // Start with entire image visible
  }, []);

  // Create a cropped image from the source and crop area
  const getCroppedImage = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<Blob | null> => {
      const image = new Image();
      image.src = imageSrc;
      await new Promise((resolve) => {
        image.onload = resolve;
      });

      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png", 1);
      });
    },
    []
  );

  // Handle crop confirmation
  const handleCropConfirm = useCallback(async () => {
    if (!imageToCrop || !croppedAreaPixels) {
      setShowImageCropper(false);
      setImageToCrop(null);
      return;
    }

    const blob = await getCroppedImage(imageToCrop, croppedAreaPixels);
    if (blob) {
      const file = new File([blob], "cropped-logo.png", { type: "image/png" });
      setVisualAssetFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVisualAssetPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    setShowImageCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(0.1);
    setCroppedAreaPixels(null);
  }, [imageToCrop, croppedAreaPixels, getCroppedImage]);

  // Cancel cropping
  const handleCropCancel = useCallback(() => {
    setShowImageCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(0.1);
    setCroppedAreaPixels(null);
  }, []);

  const handleVisualAssetChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setVisualAssetError(null);
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        setVisualAssetError(`Invalid file type. Please use PNG, JPG, GIF, or WebP.`);
        if (event.target) event.target.value = "";
        return;
      }

      if (file.size > MAX_LOGO_SIZE_BYTES) {
        setVisualAssetError(
          `File is too large. Max size is ${MAX_LOGO_SIZE_BYTES / (1024 * 1024)}MB.`
        );
        if (event.target) event.target.value = "";
        return;
      }

      // Show the cropper instead of directly setting the preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result as string);
        setShowImageCropper(true);
      };
      reader.readAsDataURL(file);
      if (event.target) event.target.value = "";
    }
  };

  const handleRemoveVisualAsset = () => {
    setVisualAssetFile(undefined);
    setVisualAssetPreview(undefined);
    setVisualAssetError(null);
    // Clear logoPath in kitData so removal persists on save
    setKitData((prev) => ({ ...prev, logoPath: undefined }));
  };

  const handleImportFromUrl = async () => {
    if (!importUrl.trim() || isImporting) return;

    const idToken = await currentUser?.getIdToken();
    if (!idToken) {
      setOverlayState("error");
      setImportError("Please sign in to import from a URL.");
      setIsBlockedError(false);
      return;
    }

    setIsImporting(true);
    setOverlayState("loading");
    setImportStep(0);
    setImportError(null);
    setIsBlockedError(false);

    try {
      if (!initialData?.id) {
        throw new Error("Brand kit ID not available");
      }
      const result = await extractBrandKitFromUrl(
        importUrl.trim(),
        idToken,
        initialData.id,
      );

      // Populate kitData with extracted fields
      setKitData((prev) => ({
        ...prev,
        name: prev.name || result.brandName || "",
        brandName: result.brandName || prev.brandName,
        tagline: result.tagline || prev.tagline,
        website: result.website || prev.website,
        // Extracted colors lead; existing colors keep their spot after them
        colors: brandColorListToSettings(
          mergeBrandColorLists(
            getBrandColorList(result.colors),
            getBrandColorList(prev.colors)
          )
        ),
        fonts: {
          primaryFont: result.fonts.primaryFont || prev.fonts?.primaryFont || null,
          secondaryFont: result.fonts.secondaryFont || prev.fonts?.secondaryFont || null,
          bodyFont: result.fonts.bodyFont || prev.fonts?.bodyFont || null,
        },
        brandVoiceTone: result.voiceTone || prev.brandVoiceTone,
        brandVoiceInstructions: result.voiceInstructions || prev.brandVoiceInstructions,
        businessDescription: result.businessDescription || prev.businessDescription,
        targetAudiences: result.targetAudiences?.length ? result.targetAudiences : prev.targetAudiences,
        primarySocial: result.primarySocial
          ? { platform: result.primarySocial.platform as any, handle: result.primarySocial.handle }
          : prev.primarySocial,
      }));

      // Set logo preview if extracted
      if (result.logoPath) {
        setKitData((prev) => ({ ...prev, logoPath: result.logoPath! }));
        setVisualAssetFile(undefined);
        setVisualAssetPreview(undefined);
      }

      // Add extracted brand assets — only those whose thumbnails actually load (parallel)
      if (result.brandAssets && result.brandAssets.length > 0) {
        const assetsWithThumbs = result.brandAssets.filter((a) => !!a.thumbnailPath);
        const results = await Promise.allSettled(
          assetsWithThumbs.map(async (asset) => {
            const thumbUrl = await getSignedUrl(asset.thumbnailPath!);
            await new Promise<void>((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => reject();
              img.src = thumbUrl;
            });
            return thumbUrl;
          })
        );

        const validAssets: typeof result.brandAssets = [];
        const validThumbnails: Record<string, string> = {};
        results.forEach((r, i) => {
          const asset = assetsWithThumbs[i];
          if (r.status === "fulfilled") {
            validAssets.push(asset);
            validThumbnails[asset.id] = r.value;
          } else {
            deleteObject(ref(storage, asset.storagePath)).catch(() => {});
            deleteObject(ref(storage, asset.thumbnailPath!)).catch(() => {});
          }
        });

        if (validAssets.length > 0) {
          setPendingUploads((prev) => [...prev, ...validAssets]);
          setBrandAssetThumbnails((prev) => ({ ...prev, ...validThumbnails }));
        }
      }

      // Success — dismiss overlay, form is now pre-filled
      setShowOverlay(false);
    } catch (error) {
      console.error("Import from URL failed:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Could not extract brand data from this URL.";
      setOverlayState("error");
      setImportError(errorMsg);
      setIsBlockedError((error as any)?.code === "SITE_BLOCKED");
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  const handleBRollChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBRollError(null);
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      const currentTotal = bRollVideos.length + existingBRollPreviews.length;

      if (currentTotal + files.length > MAX_BROLL_VIDEOS) {
        setBRollError(
          `Cannot add ${files.length} videos. Maximum ${MAX_BROLL_VIDEOS} videos allowed. You currently have ${currentTotal} videos.`
        );
        if (event.target) event.target.value = "";
        return;
      }

      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const file of files) {
        if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
          errors.push(
            `${file.name}: Invalid file type. Please use MP4, WebM, OGG, MOV, or AVI.`
          );
          continue;
        }

        if (file.size > MAX_VIDEO_SIZE_BYTES) {
          errors.push(
            `${file.name}: File is too large. Max size is ${MAX_VIDEO_SIZE_BYTES / (1024 * 1024)}MB.`
          );
          continue;
        }

        validFiles.push(file);
      }

      if (errors.length > 0) {
        setBRollError(errors.join("\n"));
      }

      if (validFiles.length > 0) {
        const newPreviews: BRollVideoPreview[] = [];

        for (const file of validFiles) {
          const id = `broll-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          const preview = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result as string);
            };
            reader.readAsDataURL(file);
          });

          newPreviews.push({
            file,
            preview,
            id,
          });
        }

        setBRollVideos((prev) => [...prev, ...newPreviews]);
      }
    }
    if (event.target) event.target.value = "";
  };

  const handleRemoveBRollVideo = (id: string) => {
    setBRollVideos((prev) => prev.filter((video) => video.id !== id));
  };

  const handleRemoveExistingBRollVideo = (index: number) => {
    const pathToRemove = (kitData.bRollVideos || [])[index];
    setKitData((prev) => ({
      ...prev,
      bRollVideos: (prev.bRollVideos || []).filter((p) => p !== pathToRemove),
    }));
    setExistingBRollPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Brand Asset Handlers
  const handleBrandAssetUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBrandAssetError(null);
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const files = Array.from(event.target.files);

    // Calculate effective count: existing - deletions + pending uploads + new files
    const effectiveCount =
      brandAssets.filter((a) => !pendingDeletions.has(a.id)).length +
      pendingUploads.length +
      files.length;

    if (effectiveCount > MAX_BRAND_ASSETS) {
      const currentCount =
        brandAssets.filter((a) => !pendingDeletions.has(a.id)).length +
        pendingUploads.length;
      setBrandAssetError(
        `Cannot add ${files.length} images. Maximum ${MAX_BRAND_ASSETS} images allowed. You currently have ${currentCount} images.`
      );
      if (event.target) event.target.value = "";
      return;
    }

    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      if (!ALLOWED_ASSET_TYPES.includes(file.type)) {
        errors.push(
          `${file.name}: Invalid file type. Please use PNG, JPG, or WebP.`
        );
        continue;
      }

      if (file.size > MAX_ASSET_SIZE_BYTES) {
        errors.push(
          `${file.name}: File is too large. Max size is ${MAX_ASSET_SIZE_BYTES / (1024 * 1024)}MB.`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      setBrandAssetError(errors.join("\n"));
    }

    if (validFiles.length === 0) {
      if (event.target) event.target.value = "";
      return;
    }

    if (!initialData?.id) {
      setBrandAssetError("Brand kit ID not available. Please try again.");
      if (event.target) event.target.value = "";
      return;
    }

    // Get auth token
    const idToken = await currentUser?.getIdToken();
    if (!idToken) {
      setBrandAssetError("Authentication required. Please sign in again.");
      if (event.target) event.target.value = "";
      return;
    }

    // Upload each file in draft mode (storage only, no Firestore until save)
    for (const file of validFiles) {
      const uploadId = `uploading-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setUploadingAssets((prev) => new Set(prev).add(uploadId));

      try {
        const result = await uploadBrandKitAsset(
          initialData.id,
          file,
          idToken,
          true // draftMode - don't add to Firestore yet
        );

        // Add to pending uploads (will be committed on save)
        setPendingUploads((prev) => [...prev, result.asset]);

        // Create a local thumbnail preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setBrandAssetThumbnails((prev) => ({
            ...prev,
            [result.asset.id]: reader.result as string,
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Failed to upload brand asset:", error);
        setBrandAssetError(getBrandKitAssetUploadErrorMessage(error));
      } finally {
        setUploadingAssets((prev) => {
          const next = new Set(prev);
          next.delete(uploadId);
          return next;
        });
      }
    }

    if (event.target) event.target.value = "";
  };

  const handleRemoveBrandAsset = async (assetId: string) => {
    // Check if this is a pending upload (not yet saved to Firestore)
    const pendingAsset = pendingUploads.find((a) => a.id === assetId);

    if (pendingAsset) {
      try {
        if (!initialData?.id || !currentUser) {
          throw new Error("Missing brand kit or user context");
        }
        const idToken = await currentUser.getIdToken();
        await deleteDraftBrandKitAssets(
          initialData.id,
          [
            {
              storagePath: pendingAsset.storagePath,
              thumbnailPath: pendingAsset.thumbnailPath,
            },
          ],
          idToken,
        );
        setBrandAssetError(null);
      } catch (error) {
        console.error("Failed to remove draft brand asset:", error);
        setBrandAssetError(
          "We couldn't remove this image. Check your connection and try again.",
        );
        return;
      }

      setPendingUploads((prev) => prev.filter((a) => a.id !== assetId));
    } else {
      // Existing asset - mark for deletion (will be deleted on save)
      setPendingDeletions((prev) => new Set(prev).add(assetId));
    }

    // Remove thumbnail preview
    setBrandAssetThumbnails((prev) => {
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
  };

  const refreshBrandAssets = async () => {
    if (!initialData?.id) return;

    try {
      const brandKitRef = doc(db, "brand_kits", initialData.id);
      const brandKitDoc = await getDoc(brandKitRef);

      if (brandKitDoc.exists()) {
        const data = brandKitDoc.data() as BrandKit;
        if (data.brandAssets) {
          // Deduplicate by asset ID (in case of prior double-writes)
          const seen = new Set<string>();
          const deduped = data.brandAssets.filter((a) => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          });
          setBrandAssets(deduped);
          // Also refresh thumbnails for any new assets
          const newThumbnails: Record<string, string> = { ...brandAssetThumbnails };
          for (const asset of data.brandAssets) {
            if (!newThumbnails[asset.id] && asset.thumbnailPath) {
              try {
                newThumbnails[asset.id] = await getSignedUrl(asset.thumbnailPath);
              } catch (e) {
                console.error(`Failed to fetch thumbnail for ${asset.id}`);
              }
            }
          }
          setBrandAssetThumbnails(newThumbnails);

          // Show status summary
          const statusCounts = data.brandAssets.reduce((acc, asset) => {
            acc[asset.processingStatus] = (acc[asset.processingStatus] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          // Removed "Status Refreshed" toast to avoid spamming user during auto-polling
        }
      }
    } catch (error) {
      console.error("Failed to refresh brand assets:", error);
      toast({
        variant: "destructive",
        title: "Refresh Failed",
        description: "Failed to fetch latest status",
      });
    }
  };

  const handleFontChange = (
    fontType: keyof BrandKitFontSettings,
    value: string
  ) => {
    const newFonts = { ...(kitData.fonts || defaultFontSettings) };
    if (value === NO_FONT_SELECTED_VALUE) {
      newFonts[fontType] = null;
    } else {
      newFonts[fontType] = value;
    }
    setKitData((prev) => ({ ...prev, fonts: newFonts }));
  };

  const handlePaletteChange = (newColors: BrandKitColorSettings) => {
    setKitData((prev) => ({ ...prev, colors: newColors }));
  };

  const handlePairNow = async () => {
    if (!initialData?.id || isPairing) return;
    setIsPairing(true);
    try {
      const idToken = await currentUser?.getIdToken();
      if (!idToken) throw new Error("Not signed in");
      const system = await refreshBrandKitColorSystem(
        initialData.id,
        idToken,
        { colors: kitData.colors }
      );
      setDerivedColorSystem(system);
    } catch (error) {
      console.error("Pair now failed:", error);
      toast({
        variant: "destructive",
        title: "Pairing failed",
        description: "Couldn't create color pairings. Please try again.",
      });
    } finally {
      setIsPairing(false);
    }
  };

  const handleAddAudience = () => {
    const value = newAudience.trim();
    if (!value || isSaving || (kitData.targetAudiences || []).length >= 7) return;
    setKitData((prev) => ({
      ...prev,
      targetAudiences: [...(prev.targetAudiences || []), value].slice(0, 7),
    }));
    setNewAudience("");
  };

  const handleSubmit = async () => {
    if (!kitData.name?.trim()) {
      setKitData((prev) => ({ ...prev, name: "Untitled Brand Kit" }));
      kitData.name = "Untitled Brand Kit";
    }
    if (visualAssetError) {
      toast({
        variant: "destructive",
        title: "Visual Asset Error",
        description: visualAssetError,
      });
      return;
    }
    if (coverBgError) {
      toast({
        variant: "destructive",
        title: "Cover Image Error",
        description: coverBgError,
      });
      return;
    }
    if (bRollError) {
      toast({
        variant: "destructive",
        title: "B-Roll Error",
        description: bRollError,
      });
      return;
    }

    setIsSaving(true);

    const filesToUpload = {
      visualAsset: visualAssetFile,
      cover: coverBgFile,
      bRoll: bRollVideos.map((v) => v.file),
    };

    try {
      // Get auth token for brand asset operations
      const idToken = await currentUser?.getIdToken();

      // Prepare final assets list for onSave
      let finalBrandAssets = [...brandAssets];

      // Determine if this is a genuinely new kit (skeleton) vs editing a saved kit.
      // New kits have an ID but their Firestore doc doesn't exist yet,
      // so we can't use commitBrandKitAssets (which updates the doc).
      const isNewKit = !initialData?.name;

      // Apply additions and deletions together so the server validates the
      // final count instead of temporarily counting replacement images twice.
      if (initialData?.id && idToken && !isNewKit) {
        console.log("BrandKitModal: Handling asset changes. Pending uploads:", pendingUploads.length, "Pending deletions:", pendingDeletions.size);

        if (pendingUploads.length > 0 || pendingDeletions.size > 0) {
          const deletionIds = Array.from(pendingDeletions);
          await commitBrandKitAssets(
            initialData.id,
            pendingUploads,
            idToken,
            deletionIds
          );

          finalBrandAssets = [
            ...finalBrandAssets.filter((a) => !pendingDeletions.has(a.id)),
            ...pendingUploads,
          ];
          setBrandAssets(finalBrandAssets);
          setPendingUploads([]);
          setPendingDeletions(new Set());
        }
      } else if (isNewKit) {
        // For new kits, merge pending uploads into the final list
        // (they'll be saved with setDoc and committed after doc creation)
        finalBrandAssets = [
          ...finalBrandAssets,
          ...pendingUploads.filter((a) => !pendingDeletions.has(a.id)),
        ];
      }

      // Ensure kitData has the correct brandAssets list before saving
      const finalKitData: Partial<EditableBrandKit> = {
        ...kitData,
        targetAudiences: [...(kitData.targetAudiences || []), newAudience]
          .map((audience) => audience.trim())
          .filter(Boolean)
          .slice(0, 7),
      };
      // Server-owned: derived fresh from palette + roles after save
      delete (finalKitData as any).colorSystem;

      // For new kits: include brandAssets so they get written with setDoc.
      // For existing kits: exclude brandAssets since commitBrandKitAssets handled them.
      if (isNewKit) {
        console.log("BrandKitModal: New kit, including brandAssets in save payload");
        finalKitData.brandAssets = finalBrandAssets;
      } else {
        console.log("BrandKitModal: Existing kit, EXCLUDING brandAssets from save payload to avoid overwrite");
        delete (finalKitData as any).brandAssets;
      }

      console.log("BrandKitModal: Calling onSave with:", { finalKitData, filesToUpload });
      await onSave(finalKitData, filesToUpload);
      hasSavedRef.current = true;
    } catch (error) {
      console.error("Brand kit save failed:", error);
      const errorCopy = getBrandKitSaveErrorCopy(error);
      toast({
        variant: "destructive",
        title: errorCopy.title,
        description: errorCopy.description,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = async (open: boolean) => {
    if (!open) {
      // At the import/create-manually step nothing has been entered yet —
      // closing here should not create an empty "Untitled Brand Kit"
      const atCreationOverlay = showOverlay && !initialData?.name;
      if (atCreationOverlay) {
        onClose();
      } else if (!isSaving && !hasSavedRef.current) {
        await handleSubmit();
      }
      setTimeout(() => setView("main"), 300);
    }
  };

  const totalBRollCount =
    bRollVideos.length + (kitData.bRollVideos?.length || 0);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[1000px] h-[90vh] flex flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-0 border-b px-6 py-4 pr-14 text-left">
          {showOverlay && !initialData?.name ? (
            <>
              <DialogTitle className="text-xl font-semibold leading-tight">
                Create Brand Kit
              </DialogTitle>
              <p className="pt-1 text-sm text-muted-foreground">
                Start from your website, or build it by hand
              </p>
            </>
          ) : (
            <>
              <DialogTitle className="sr-only">
                {initialData?.name ? "Edit Brand Kit" : "Create Brand Kit"}
              </DialogTitle>
              <div className="flex min-w-0 items-center">
                <input
                  ref={kitNameInputRef}
                  value={kitData.name || ""}
                  onChange={(e) =>
                    setKitData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Untitled Brand Kit"
                  aria-label="Brand kit name"
                  style={{
                    width: `${Math.min(
                      Math.max(
                        (kitData.name || "Untitled Brand Kit").length + 2,
                        12
                      ),
                      50
                    )}ch`,
                  }}
                  className="-mx-1.5 max-w-full rounded-md bg-transparent px-1.5 text-xl font-semibold leading-tight outline-none transition-colors placeholder:text-muted-foreground/50 hover:bg-muted/60 focus:bg-muted/40"
                />
                <button
                  type="button"
                  onClick={() => {
                    kitNameInputRef.current?.focus();
                    kitNameInputRef.current?.select();
                  }}
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Rename brand kit"
                  title="Rename"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="pt-1 text-sm text-muted-foreground">
                Everything WaveGen creates for this brand follows this kit
              </p>
            </>
          )}
        </DialogHeader>
          <div className="relative flex min-h-0 flex-1">
            {/* Overlay for new brand kit creation */}
            {showOverlay && !initialData?.name && (
              <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm flex items-center justify-center transition-opacity duration-300">
                <div className="w-full max-w-sm mx-auto px-4">
                  {overlayState === "input" && (
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Globe className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Import from Website</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Paste a URL to auto-fill colors, fonts, logo, and brand voice
                        </p>
                      </div>
                      <div className="flex w-full gap-2">
                        <Input
                          value={importUrl}
                          onChange={(e) => setImportUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleImportFromUrl();
                            }
                          }}
                          placeholder="example.com"
                          className="flex-1"
                          autoFocus
                        />
                        <Button
                          type="button"
                          onClick={handleImportFromUrl}
                          disabled={!importUrl.trim()}
                        >
                          Go
                        </Button>
                      </div>
                      <div className="flex items-center w-full gap-3">
                        <Separator className="flex-1" />
                        <span className="text-xs text-muted-foreground">or</span>
                        <Separator className="flex-1" />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowOverlay(false)}
                      >
                        Create Manually
                      </Button>
                    </div>
                  )}

                  {overlayState === "loading" && (
                    <div className="flex flex-col items-center text-center space-y-4">
                      <Loader2 className="h-10 w-10 animate-spin text-primary" />
                      <div>
                        <p className="text-sm font-medium">
                          {IMPORT_STEPS[importStep]}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Step {importStep + 1} of {IMPORT_STEPS.length}
                        </p>
                      </div>
                    </div>
                  )}

                  {overlayState === "error" && (
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                        <AlertCircle className="h-6 w-6 text-destructive" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">
                          {isBlockedError ? "Site Blocked Auto-Access" : "Import Failed"}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {isBlockedError
                            ? "This website blocks automated access, so we can't extract brand data from it. You can still create your brand kit manually."
                            : importError}
                        </p>
                      </div>
                      <div className="flex flex-col items-center gap-2">
                        {!isBlockedError && (
                          <Button
                            type="button"
                            onClick={() => {
                              setOverlayState("input");
                              setImportError(null);
                              setIsBlockedError(false);
                            }}
                          >
                            Try Again
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant={isBlockedError ? "default" : "outline"}
                          onClick={() => setShowOverlay(false)}
                        >
                          Create Manually
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section rail */}
            <aside className="hidden w-56 shrink-0 flex-col overflow-y-auto border-r bg-muted/40 px-3 py-4 md:flex">
              <nav className="space-y-1" aria-label="Brand kit sections">
                {EDITOR_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      activeSection === section.id
                        ? "bg-background font-medium text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-background/60 hover:text-foreground"
                    )}
                    aria-current={
                      activeSection === section.id ? "true" : undefined
                    }
                  >
                    <section.icon className="h-4 w-4 shrink-0" />
                    {section.id === "templates" && looks ? "Looks" : section.label}
                  </button>
                ))}
              </nav>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
              {/* Compact section switcher for small screens */}
              <div className="flex shrink-0 gap-1 overflow-x-auto border-b px-3 py-2 md:hidden">
                {EDITOR_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors",
                      activeSection === section.id
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {section.id === "templates" && looks ? "Looks" : section.label}
                  </button>
                ))}
              </div>

              <main className="flex-1 overflow-y-auto px-6 py-5">
            {/* SECTION: Identity */}
            {activeSection === "identity" && (
            <section className="space-y-5">
              <header className="space-y-1">
                <h3 className="text-base font-semibold">Brand Identity</h3>
                <p className="text-sm text-muted-foreground">
                  This information represents you or your business
                </p>
              </header>
              <div className="space-y-4">
                {/* Name */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label htmlFor="brand-name" className="sm:text-right">
                    Name
                  </Label>
                  <div className="sm:col-span-3">
                    <Input
                      id="brand-name"
                      value={kitData.brandName || ""}
                      onChange={(e) =>
                        setKitData((prev) => ({
                          ...prev,
                          brandName: e.target.value,
                        }))
                      }
                      placeholder="Your name or company name"
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                      e.g., "Jane Smith" or "ACME Consulting"
                    </p>
                  </div>
                </div>

                {/* Visual Asset (Photo or Logo) */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label htmlFor="visual-asset" className="sm:text-right">
                    Visual Asset
                  </Label>
                  <div className="sm:col-span-3">
                    {visualAssetPreview || kitData.logoPath ? (
                      <div className="flex items-center gap-4">
                        <div className="relative h-24 w-24 flex-shrink-0">
                          {/* Contrasting circle behind the logo (dark for a white logo) */}
                          <div
                            className={`absolute inset-0 rounded-full shadow-md ${logoSurfaceClass(visualAssetTone)}`}
                            style={{
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            }}
                          />
                          {/* Logo on top - object-contain to show full logo */}
                          {visualAssetPreview ? (
                          <img
                            src={visualAssetPreview}
                            onError={() => setVisualAssetError("Couldn’t load logo preview.")}
                            alt="Brand visual"
                            className="absolute rounded-full object-contain"
                            style={{
                              top: "4px",
                              left: "4px",
                              width: "88px",
                              height: "88px",
                            }}
                          />
                          ) : (
                            <div
                              className="absolute inset-0 flex items-center justify-center"
                              role="status"
                              aria-label={logoLoading ? "Loading saved logo" : "Logo preview unavailable"}
                            >
                              {logoLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                              ) : (
                                <Images className="h-6 w-6 text-muted-foreground" />
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {logoPreviewError && !localVisualAssetPreview && (
                            <p className="text-xs text-muted-foreground">
                              Couldn’t load your saved logo.{" "}
                              <button type="button" onClick={retryLogo} className="underline underline-offset-2">
                                Retry
                              </button>
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => visualAssetInputRef.current?.click()}
                              className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent transition-colors"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={handleRemoveVisualAsset}
                              className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPG, GIF, or WebP. Max 5MB.
                          </p>
                        </div>
                        <input
                          ref={visualAssetInputRef}
                          type="file"
                          onChange={handleVisualAssetChange}
                          accept={ALLOWED_LOGO_TYPES.join(",")}
                          className="hidden"
                        />
                      </div>
                    ) : (
                      <>
                        <Input
                          id="visual-asset"
                          type="file"
                          onChange={handleVisualAssetChange}
                          accept={ALLOWED_LOGO_TYPES.join(",")}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Upload your profile photo or brand logo. PNG, JPG, GIF, or WebP.
                          Max 5MB.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {visualAssetError && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-1.5 sm:gap-4">
                    <div className="hidden sm:block"></div>
                    <div className="sm:col-span-3">
                      <p className="text-sm text-red-600">{visualAssetError}</p>
                    </div>
                  </div>
                )}

                {/* Tagline */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label htmlFor="brand-tagline" className="sm:text-right">
                    Tagline
                    <span className="text-muted-foreground text-xs ml-1">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="brand-tagline"
                    value={kitData.tagline || ""}
                    onChange={(e) =>
                      setKitData((prev) => ({
                        ...prev,
                        tagline: e.target.value,
                      }))
                    }
                    className="sm:col-span-3"
                    placeholder="What you do or your mission"
                  />
                </div>

                <Separator className="my-4" />

                {/* Website */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label htmlFor="website" className="sm:text-right">
                    Website
                  </Label>
                  <Input
                    id="website"
                    value={kitData.website || ""}
                    onChange={(e) =>
                      setKitData((prev) => ({
                        ...prev,
                        website: e.target.value,
                      }))
                    }
                    className="sm:col-span-3"
                    placeholder="yourwebsite.com"
                  />
                </div>

                {/* Primary Social */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label className="sm:text-right">Primary Social</Label>
                  <div className="sm:col-span-3 flex flex-col sm:flex-row gap-2">
                    <Select
                      value={kitData.primarySocial?.platform || ""}
                      onValueChange={(value) =>
                        setKitData((prev) => ({
                          ...prev,
                          primarySocial: {
                            platform: value as any,
                            handle: prev.primarySocial?.handle || "",
                          },
                        }))
                      }
                    >
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linkedin">
                          <div className="flex items-center gap-2">
                            <FaLinkedin className="w-4 h-4 text-[#0A66C2]" />
                            <span>LinkedIn</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="instagram">
                          <div className="flex items-center gap-2">
                            <Instagram className="w-4 h-4 text-[#E4405F]" />
                            <span>Instagram</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="twitter">
                          <div className="flex items-center gap-2">
                            <FaXTwitter className="w-4 h-4" />
                            <span>Twitter/X</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="youtube">
                          <div className="flex items-center gap-2">
                            <Youtube className="w-4 h-4 text-[#FF0000]" />
                            <span>YouTube</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="tiktok">
                          <div className="flex items-center gap-2">
                            <FaTiktok className="w-4 h-4" />
                            <span>TikTok</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1 flex items-center border rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                      <span className="px-3 py-2 bg-muted text-muted-foreground border-r select-none">
                        @
                      </span>
                      <Input
                        value={kitData.primarySocial?.handle || ""}
                        onChange={(e) =>
                          setKitData((prev) => ({
                            ...prev,
                            primarySocial: {
                              platform:
                                prev.primarySocial?.platform || "linkedin",
                              handle: e.target.value,
                            },
                          }))
                        }
                        placeholder="yourhandle"
                        className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                  </div>
                </div>

                {/* Preferred branding */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                  <Label className="sm:text-right">Preferred Branding</Label>
                  <div className="sm:col-span-3">
                    <Select
                      value={kitData.primaryBrandElement || "auto"}
                      onValueChange={(value) =>
                        setKitData((prev) => ({
                          ...prev,
                          primaryBrandElement: value as PrimaryBrandElement,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIMARY_BRAND_ELEMENT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Choose what output should show when there is only room
                      for one brand detail.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* SECTION: Colors & Type */}
            {activeSection === "style" && (
            <section className="space-y-5">
              <header className="space-y-1">
                <h3 className="text-base font-semibold">Colors & Type</h3>
                <p className="text-sm text-muted-foreground">
                  Applied to everything WaveGen generates for this brand
                </p>
              </header>
              <div className="space-y-8">
                {/* Colors */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Palette</h4>
                  <BrandPaletteEditor
                    colors={kitData.colors}
                    onChange={handlePaletteChange}
                  />
                </div>

                {/* Pairings — only once the system applies (4+ colors) */}
                {(getBrandColorList(kitData.colors).length > 3 ||
                  derivedColorSystem) && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium">Color pairings</h4>
                    <BrandColorSystemCard
                      system={derivedColorSystem}
                      paletteColors={getBrandColorList(kitData.colors)}
                      isPairing={isPairing}
                      canPairNow={!!initialData?.id}
                      onPairNow={handlePairNow}
                    />
                  </div>
                )}

                {/* Fonts */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Typography</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {(
                      [
                        ["primaryFont", "Heading font"],
                        ["secondaryFont", "Subheading font"],
                        ["bodyFont", "Body font"],
                      ] as Array<[keyof BrandKitFontSettings, string]>
                    ).map(([fontType, fontLabel]) => (
                      <div className="space-y-2" key={fontType}>
                        <Label htmlFor={`font-${fontType}`} className="text-sm">
                          {fontLabel}
                        </Label>
                        <div className="flex gap-2">
                          <Select
                            value={
                              kitData.fonts?.[fontType] || NO_FONT_SELECTED_VALUE
                            }
                            onValueChange={(value) =>
                              handleFontChange(fontType, value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={`Select ${fontLabel.toLowerCase()}`}
                              />
                            </SelectTrigger>
                            <SelectContent
                              style={{ maxHeight: 300, overflowY: "auto" }}
                            >
                              <SelectItem value={NO_FONT_SELECTED_VALUE}>
                                No Font
                              </SelectItem>
                              {availableFonts.map((font) => (
                                <SelectItem
                                  key={font}
                                  value={font}
                                  style={{ fontFamily: font }}
                                >
                                  {font}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => handleFontChange(fontType, NO_FONT_SELECTED_VALUE)}
                            className={`px-2 text-muted-foreground hover:text-foreground transition-colors ${!kitData.fonts?.[fontType] ? "invisible" : ""}`}
                            aria-label="Clear font"
                            title="Clear font"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Style Instructions - hidden from UI, data preserved */}
              </div>
            </section>
            )}

            {activeSection === "business" && (
              <section className="space-y-6">
                <header className="space-y-1">
                  <h3 className="text-base font-semibold">Your business & audience</h3>
                  <p className="text-sm text-muted-foreground">
                    Help Wavegen suggest relevant topics and write for the people you want to reach.
                  </p>
                </header>
                <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="business-description">About your business</Label>
                  <Textarea id="business-description" rows={4} maxLength={2000}
                    placeholder="What your business does, what you offer, and what makes it different."
                    value={kitData.businessDescription || ""}
                    onChange={e => setKitData(prev => ({ ...prev, businessDescription: e.target.value }))} />
                </div>
                <div className="space-y-3">
                  <Label htmlFor={(kitData.targetAudiences || []).length ? "target-audience-0" : "new-audience"}>
                    Target audiences
                  </Label>
                  {(kitData.targetAudiences || []).map((audience, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Textarea
                        id={`target-audience-${index}`}
                        aria-label={`Audience ${index + 1}`}
                        rows={2}
                        className="min-h-0 resize-none leading-relaxed"
                        maxLength={240}
                        value={audience}
                        disabled={isSaving}
                        onChange={(event) => setKitData((prev) => ({
                          ...prev,
                          targetAudiences: (prev.targetAudiences || []).map((value, i) =>
                            i === index ? event.target.value : value),
                        }))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        aria-label={`Remove audience ${index + 1}`}
                        disabled={isSaving}
                        onClick={() => setKitData((prev) => ({
                          ...prev,
                          targetAudiences: (prev.targetAudiences || []).filter((_, i) => i !== index),
                        }))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {(kitData.targetAudiences || []).length < 7 && (
                    <div className="flex gap-2">
                      <Input
                        id="new-audience"
                        aria-label="New audience"
                        placeholder="Add an audience (optional)"
                        maxLength={240}
                        value={newAudience}
                        disabled={isSaving}
                        onChange={(event) => setNewAudience(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                            event.preventDefault();
                            handleAddAudience();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        aria-label="Add audience"
                        disabled={isSaving || !newAudience.trim()}
                        onClick={handleAddAudience}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Changes apply to new suggestions and content generated with this brand kit.
                </p>
              </section>
            )}

            {/* SECTION: Brand Voice */}
            {activeSection === "voice" && (
            <section className="space-y-5">
              <header className="space-y-1">
                <h3 className="text-base font-semibold">Brand Voice</h3>
                <p className="text-sm text-muted-foreground">
                  Define how your content should sound and communicate
                </p>
              </header>
              <div className="space-y-4">
                {/* Tone Selector */}
                <div className="space-y-2">
                  <Label htmlFor="brand-voice-tone">Tone</Label>
                  <Select
                    value={kitData.brandVoiceTone || "none"}
                    onValueChange={(value) => {
                      const tone =
                        value === "none" ? null : (value as BrandVoiceTone);
                      setKitData((prev) => ({ ...prev, brandVoiceTone: tone }));
                    }}
                  >
                    <SelectTrigger id="brand-voice-tone">
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAND_VOICE_TONE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value || "none"}
                          value={option.value || "none"}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Set the overall tone for generated content
                  </p>
                </div>

                {/* Voice Guidelines */}
                <div className="space-y-2">
                  <Label htmlFor="voice-instructions">
                    Voice Guidelines{" "}
                    <span className="text-muted-foreground text-xs">
                      (optional)
                    </span>
                  </Label>
                  <Textarea
                    id="voice-instructions"
                    value={kitData.brandVoiceInstructions || ""}
                    onChange={(e) => {
                      const value = e.target.value.slice(
                        0,
                        MAX_INSTRUCTIONS_LENGTH
                      );
                      setKitData((prev) => ({
                        ...prev,
                        brandVoiceInstructions: value || undefined,
                      }));
                    }}
                    placeholder="Describe your brand's communication style and language preferences. E.g., 'Use clear, accessible language. Avoid jargon. Always explain the WHY behind recommendations.'"
                    className="min-h-[120px] resize-none"
                    maxLength={MAX_INSTRUCTIONS_LENGTH}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Guide the AI on how to write your content</span>
                    <span>
                      {kitData.brandVoiceInstructions?.length || 0}/
                      {MAX_INSTRUCTIONS_LENGTH}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* SECTION: Templates */}
            {activeSection === "templates" && (
            <section className="space-y-5">
              <header className="space-y-1">
                <h3 className="text-base font-semibold">{looks ? "Looks" : "Template Preferences"}</h3>
                <p className="text-sm text-muted-foreground">
                  {looks ? "Choose the looks this brand’s posts are made from" : "Control the look and feel of your generated content"}
                </p>
              </header>
              <div className="space-y-4">
                {/* Toggle for restricting templates */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="restrict-templates"
                      className="text-sm font-medium"
                    >
                      Use a fixed set of {looks ? "looks" : "templates"} for this brand
                    </Label>
                  </div>
                  <Switch
                    id="restrict-templates"
                    checked={restrictTemplates}
                    onCheckedChange={handleRestrictTemplatesToggle}
                  />
                </div>

                {/* Template selection - only show when restriction is enabled */}
                {restrictTemplates && (
                  <>
                    {templatesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : availableTemplates.length > 0 ? (
                      <>
                        {/* Category Tabs */}
                        {!looks && categoriesForType.length > 1 && (
                          <Tabs
                            value={activeCategoryTab}
                            onValueChange={setActiveCategoryTab}
                          >
                            <TabsList className="w-full justify-start">
                              {categoriesForType.map((cat) => (
                                <TabsTrigger
                                  key={cat}
                                  value={cat}
                                  className="capitalize"
                                >
                                  {cat}
                                  {selectedCountByCategory[cat] > 0 && (
                                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-xs">
                                      {selectedCountByCategory[cat]}
                                    </span>
                                  )}
                                </TabsTrigger>
                              ))}
                            </TabsList>
                          </Tabs>
                        )}

                        {/* Selection Info & Actions */}
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-muted-foreground">
                            {selectedTemplateIds.size > 0 ? (
                              <span>
                                <span className="font-medium text-foreground">
                                  {selectedTemplateIds.size}
                                </span>{" "}
                                {looks ? "look" : "template"}
                                {selectedTemplateIds.size !== 1 ? "s" : ""}{" "}
                                selected
                              </span>
                            ) : (
                              <span className="text-amber-600">
                                No {looks ? "looks" : "templates"} selected - please select at least
                                one
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleSelectAllInCategory(activeCategoryTab)
                              }
                            >
                              Select all
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleClearCategory(activeCategoryTab)
                              }
                            >
                              Clear
                            </Button>
                          </div>
                        </div>

                        {/* Template Grid */}
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                          {filteredTemplates.map((t) => {
                            const isSelected = selectedTemplateIds.has(t.id);
                            const aspect = t.firstSlideDimensions;
                            return (
                              <div key={t.id} className="relative group">
                                <button
                                  type="button"
                                  className={cn(
                                    "text-left rounded-lg hover:shadow-md border-2 w-full transition-all duration-200 overflow-hidden bg-card",
                                    isSelected
                                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-primary"
                                      : "border-border hover:border-primary/50"
                                  )}
                                  onClick={() => handleTemplateToggle(t.id)}
                                >
                                  <div className="relative">
                                    <TemplateThumb
                                      example={t.example}
                                      aspect={aspect}
                                      isVideo={false}
                                      coverTimestamp={t.coverTimestamp}
                                      templateId={t.id}
                                      currentIndex={
                                        templatePreviewIndex[t.id] ?? 0
                                      }
                                      urlCache={firebaseUrlCache}
                                    />

                                    {/* Selection indicator */}
                                    <div
                                      className={cn(
                                        "absolute top-1.5 right-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all shadow",
                                        isSelected
                                          ? "bg-primary border-primary scale-100"
                                          : "bg-white/90 border-white/60 scale-0 group-hover:scale-100"
                                      )}
                                    >
                                      {isSelected && (
                                        <svg
                                          className="w-3 h-3 text-primary-foreground"
                                          fill="none"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="3"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path d="M5 13l4 4L19 7"></path>
                                        </svg>
                                      )}
                                    </div>

                                    {/* Carousel navigation for multi-image templates */}
                                    {t.example?.imagePaths &&
                                      t.example.imagePaths.length > 1 && (
                                        <>
                                          {/* Left/Right Arrow Buttons */}
                                          <button
                                            type="button"
                                            onClick={(e) =>
                                              handleTemplatePreviewPrev(
                                                e,
                                                t.id,
                                                t.example!.imagePaths!.length
                                              )
                                            }
                                            className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                            aria-label="Previous slide"
                                          >
                                            <svg
                                              className="w-3 h-3 text-white"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 19l-7-7 7-7"
                                              />
                                            </svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) =>
                                              handleTemplatePreviewNext(
                                                e,
                                                t.id,
                                                t.example!.imagePaths!.length
                                              )
                                            }
                                            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                            aria-label="Next slide"
                                          >
                                            <svg
                                              className="w-3 h-3 text-white"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                              />
                                            </svg>
                                          </button>
                                          {/* Clickable Dots */}
                                          <div className="absolute bottom-1.5 left-0 right-0 flex items-center justify-center z-10">
                                            <div className="flex gap-1 bg-black/50 px-1.5 py-1 rounded-full backdrop-blur-sm">
                                              {t.example.imagePaths.map(
                                                (_: string, i: number) => (
                                                  <button
                                                    key={i}
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      handleTemplatePreviewChange(
                                                        t.id,
                                                        i
                                                      );
                                                    }}
                                                    className={cn(
                                                      "h-1.5 rounded-full transition-all hover:bg-white/80",
                                                      (templatePreviewIndex[
                                                        t.id
                                                      ] ?? 0) === i
                                                        ? "bg-white w-2.5"
                                                        : "bg-white/50 w-1.5"
                                                    )}
                                                    aria-label={`Go to slide ${i + 1}`}
                                                  />
                                                )
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                  </div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No templates available</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
            )}

            {/* SECTION: Brand Assets */}
            {activeSection === "assets" && (
            <section className="space-y-5">
              <header className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold">Brand Assets</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload images for use in generated posts
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Tip: Brand images are used as backgrounds — avoid images with text.
                  </p>
                </div>
                <span className="shrink-0 text-sm text-muted-foreground">
                  {brandAssets.filter((a) => !pendingDeletions.has(a.id)).length + pendingUploads.length} / {MAX_BRAND_ASSETS} images
                  {(pendingUploads.length > 0 || pendingDeletions.size > 0) && (
                    <span className="text-amber-600 ml-1 text-xs font-medium">(Save to apply changes)</span>
                  )}
                </span>
              </header>
              <div>
                {(brandAssets.filter((a) => !pendingDeletions.has(a.id)).length + pendingUploads.length) === 0 && uploadingAssets.size === 0 ? (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) {
                        const mockEvent = {
                          target: { files, value: "" },
                        } as any;
                        handleBrandAssetUpload(mockEvent);
                      }
                    }}
                    onClick={() => {
                      const input = document.getElementById("brand-asset-upload");
                      if (input) input.click();
                    }}
                  >
                    <div className="space-y-3">
                      <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-gray-900">
                          Upload Brand Images
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Drag and drop images here, or click to select
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PNG, JPG, WebP • Max 10MB • Up to {MAX_BRAND_ASSETS} images
                        </p>
                      </div>
                    </div>
                    <Input
                      id="brand-asset-upload"
                      type="file"
                      multiple
                      onChange={handleBrandAssetUpload}
                      className="hidden"
                      accept={ALLOWED_ASSET_TYPES.join(",")}
                      disabled={brandAssets.length >= MAX_BRAND_ASSETS}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-3">
                      {/* Existing assets (excluding pending deletions) */}
                      {brandAssets
                        .filter((asset) => !pendingDeletions.has(asset.id))
                        .map((asset) => (
                        <div
                          key={asset.id}
                          className="relative group"
                          title={asset.description || "Processing..."}
                        >
                          <img
                            src={brandAssetThumbnails[asset.id] || ""}
                            alt={asset.description || "Brand image"}
                            className="w-full aspect-square object-cover rounded-lg border bg-gray-100"
                          />
                          {/* Status indicator */}
                          <div className="absolute bottom-1 left-1 z-10">
                            {asset.processingStatus === "pending" || asset.processingStatus === "processing" ? (
                              <div className="flex items-center gap-1 bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span>Processing</span>
                              </div>
                            ) : asset.processingStatus === "ready" ? (
                              <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : asset.processingStatus === "failed" ? (
                              <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center" title={asset.processingError}>
                                <span className="text-white text-xs font-bold">!</span>
                              </div>
                            ) : null}
                          </div>
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveBrandAsset(asset.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs z-10"
                            aria-label="Remove image"
                          >
                            ×
                          </button>
                          {/* Description tooltip on hover */}
                          {asset.description && (
                            <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg">
                              <p className="text-[10px] text-white line-clamp-2">
                                {asset.description}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Pending uploads (not yet saved) */}
                      {pendingUploads.map((asset) => (
                        <div
                          key={asset.id}
                          className="relative group"
                          title="Will be saved when you save the brand kit"
                        >
                          <img
                            src={brandAssetThumbnails[asset.id] || ""}
                            alt="New image (pending save)"
                            className="w-full aspect-square object-cover rounded-lg border-2 border-dashed border-blue-400 bg-gray-100"
                          />
                          {/* Pending save indicator */}
                          <div className="absolute bottom-1 left-1 z-10">
                            <div className="flex items-center gap-1 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                              <span>Pending Analysis</span>
                            </div>
                          </div>
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveBrandAsset(asset.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs z-10"
                            aria-label="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {/* Uploading placeholders */}
                      {Array.from(uploadingAssets).map((id) => (
                        <div
                          key={id}
                          className="w-full aspect-square bg-gray-100 rounded-lg border flex items-center justify-center"
                        >
                          <svg className="animate-spin h-6 w-6 text-gray-400" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        </div>
                      ))}

                      {/* Add more button */}
                      {(brandAssets.filter((a) => !pendingDeletions.has(a.id)).length + pendingUploads.length) < MAX_BRAND_ASSETS && (
                        <div
                          className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors cursor-pointer group"
                          onClick={() => {
                            const input = document.getElementById("brand-asset-upload-more");
                            if (input) input.click();
                          }}
                        >
                          <div className="text-center">
                            <Plus className="h-6 w-6 mx-auto text-gray-400 group-hover:text-gray-600" />
                            <p className="text-xs text-gray-500 mt-1">Add More</p>
                          </div>
                          <Input
                            id="brand-asset-upload-more"
                            type="file"
                            multiple
                            onChange={handleBrandAssetUpload}
                            className="hidden"
                            accept={ALLOWED_ASSET_TYPES.join(",")}
                          />
                        </div>
                      )}
                    </div>

                    {/* Pending Uploads Info Banner */}
                    {pendingUploads.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-3">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-amber-800">
                              <span className="font-medium">Ready to process:</span> {pendingUploads.length} new image{pendingUploads.length !== 1 ? 's' : ''}. 
                              Analysis starts on save. Only processed images are available for generation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Info message for processing status */}
                    {brandAssets.some((a) => a.processingStatus === "pending" || a.processingStatus === "processing") && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-blue-800">
                              Some images are still being processed. You can save and close this modal - processed images will be available for post generation once ready.
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="mt-2 h-7 text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-2"
                              onClick={refreshBrandAssets}
                            >
                              Refresh Status
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {brandAssetError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600 whitespace-pre-line">
                      {brandAssetError}
                    </p>
                  </div>
                )}
              </div>
            </section>
            )}
              </main>
            </div>
          </div>
        {!(showOverlay && !initialData?.name) && (
          <DialogFooter className="shrink-0 items-center border-t px-6 py-3 sm:justify-between">
            <p className="hidden text-xs text-muted-foreground sm:block">
              Changes are saved when you close
            </p>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving ? "Saving..." : "Save & Close"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>

    {/* Image Cropper Dialog */}
    <Dialog open={showImageCropper} onOpenChange={(open) => !open && handleCropCancel()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Crop Your Logo</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Drag to position and use the slider to zoom. The circular area will be your logo.
          </p>
        </DialogHeader>
        <div className="relative h-[350px] bg-muted rounded-lg overflow-hidden">
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={3}
              aspect={1}
              cropShape="round"
              showGrid={false}
              objectFit="contain"
              restrictPosition={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onMediaLoaded={onMediaLoaded}
            />
          )}
        </div>
        <div className="flex items-center gap-4 px-2">
          <span className="text-sm text-muted-foreground">Zoom</span>
          <input
            type="range"
            min={minZoom}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCropCancel}>
            Cancel
          </Button>
          <Button onClick={handleCropConfirm}>
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
